import { prefersReducedMotion } from '@theme/utilities';

const NEXT_SECTION_HASH = '#next-section';

/**
 * Smoothly scroll so the target sits at the top of the viewport (under header).
 * Relies on `.shopify-section { scroll-margin-top: var(--header-height) }`.
 * @param {Element} element
 */
function scrollToTarget(element) {
  if (!(element instanceof HTMLElement)) return;

  element.scrollIntoView({
    behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    block: 'start',
  });
}

/**
 * Finds the next `.shopify-section` after the section that contains the link.
 * @param {Element} link
 * @returns {HTMLElement | null}
 */
function getNextSection(link) {
  const currentSection = link.closest('.shopify-section');
  if (!currentSection) return null;

  let sibling = currentSection.nextElementSibling;
  while (sibling) {
    if (sibling instanceof HTMLElement && sibling.classList.contains('shopify-section')) {
      return sibling;
    }
    sibling = sibling.nextElementSibling;
  }

  return null;
}

/**
 * @param {string} hash
 * @returns {HTMLElement | null}
 */
function getHashTarget(hash) {
  if (!hash || hash === '#' || hash === NEXT_SECTION_HASH) return null;

  const id = decodeURIComponent(hash.slice(1));
  if (!id) return null;

  const byId = document.getElementById(id);
  if (byId instanceof HTMLElement) return byId;

  try {
    const named = document.querySelector(`[name="${CSS.escape(id)}"]`);
    return named instanceof HTMLElement ? named : null;
  } catch {
    return null;
  }
}

/**
 * @param {MouseEvent} event
 */
function handleAnchorClick(event) {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!(link instanceof HTMLAnchorElement)) return;

  const hrefAttr = link.getAttribute('href');
  if (!hrefAttr || !hrefAttr.startsWith('#')) return;

  // Ignore empty / placeholder hashes
  if (hrefAttr === '#' || hrefAttr === '#!') return;

  if (hrefAttr === NEXT_SECTION_HASH) {
    const nextSection = getNextSection(link);
    if (!nextSection) return;

    event.preventDefault();
    scrollToTarget(nextSection);
    return;
  }

  const target = getHashTarget(hrefAttr);
  if (!target) return;

  event.preventDefault();
  scrollToTarget(target);

  if (history.replaceState) {
    history.replaceState(null, '', hrefAttr);
  }
}

/**
 * On load / back-forward, honour a real hash target.
 */
function scrollToHashOnLoad() {
  const { hash } = window.location;
  if (!hash || hash === NEXT_SECTION_HASH) return;

  const target = getHashTarget(hash);
  if (!target) return;

  // Wait a tick for layout / header height vars
  requestAnimationFrame(() => scrollToTarget(target));
}

document.addEventListener('click', handleAnchorClick);
window.addEventListener('DOMContentLoaded', scrollToHashOnLoad);
window.addEventListener('load', scrollToHashOnLoad);
